import { useState, useEffect } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { usePageHeader } from '@/hooks/usePageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, AlertTriangle, Copy, ExternalLink } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface DiagnosticResult {
  name: string;
  status: 'success' | 'error' | 'warning';
  message: string;
  details?: string;
}

export default function SpotifySetup() {
  // Registrar header no contexto - será renderizado FORA do main pelo AppShell
  usePageHeader(
    <PageHeader 
      title="Configuração Spotify"
      subtitle="Diagnóstico e instruções"
    />
  );
  
  const [diagnostics, setDiagnostics] = useState<DiagnosticResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [spotifyConfig, setSpotifyConfig] = useState<any>(null);

  const platform = Capacitor.getPlatform();
  const isNative = Capacitor.isNativePlatform();

  const runDiagnostics = async () => {
    setIsRunning(true);
    const results: DiagnosticResult[] = [];

    try {
      // 1. Verificar plataforma
      results.push({
        name: 'Plataforma detectada',
        status: 'success',
        message: `${isNative ? 'Nativo' : 'Web'} (${platform})`,
        details: isNative ? 'App rodando como aplicativo nativo' : 'App rodando no navegador'
      });

      // 2. Buscar configuração do Spotify
      const { data: config, error: configError } = await supabase.functions.invoke('get-spotify-config', {
        body: { platform }
      });

      if (configError || !config) {
        results.push({
          name: 'Configuração do Spotify',
          status: 'error',
          message: 'Falha ao buscar configuração',
          details: configError?.message || 'Erro desconhecido'
        });
      } else {
        setSpotifyConfig(config);
        
        results.push({
          name: 'Client ID',
          status: 'success',
          message: `${config.clientId.substring(0, 8)}...`,
          details: 'Client ID configurado corretamente'
        });

        results.push({
          name: 'Redirect URI',
          status: config.redirectUri ? 'success' : 'error',
          message: config.redirectUri || 'Não configurado',
          details: config.redirectUri
        });

        // 3. Verificar se é deep link em plataforma nativa
        const isDeepLink = config.redirectUri?.startsWith('youbetterrun://');
        if (isNative && !isDeepLink) {
          results.push({
            name: 'Deep Link',
            status: 'warning',
            message: 'Deep link não está sendo usado',
            details: 'Em plataformas nativas, deve usar youbetterrun://oauth/spotify/callback'
          });
        } else if (isNative && isDeepLink) {
          results.push({
            name: 'Deep Link',
            status: 'success',
            message: 'Deep link configurado',
            details: config.redirectUri
          });
        }
      }

      // 4. Verificar conexão existente
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: connection } = await supabase
          .from('music_connections')
          .select('*')
          .eq('user_id', user.id)
          .eq('provider', 'spotify')
          .maybeSingle();

        if (connection) {
          results.push({
            name: 'Conexão existente',
            status: 'success',
            message: 'Spotify já conectado',
            details: `Última atualização: ${new Date(connection.updated_at).toLocaleString()}`
          });
        } else {
          results.push({
            name: 'Conexão existente',
            status: 'warning',
            message: 'Spotify não conectado',
            details: 'Nenhuma conexão ativa encontrada'
          });
        }
      }

      // 5. Verificar sessionStorage
      const pkceVerifier = sessionStorage.getItem('spotify_pkce_verifier');
      if (pkceVerifier) {
        results.push({
          name: 'Sessão OAuth',
          status: 'warning',
          message: 'Sessão OAuth ativa detectada',
          details: 'Pode indicar autenticação pendente ou incompleta'
        });
      }

    } catch (error) {
      logger.error('Diagnostic error', error);
      results.push({
        name: 'Diagnóstico',
        status: 'error',
        message: 'Erro ao executar diagnóstico',
        details: error instanceof Error ? error.message : 'Erro desconhecido'
      });
    }

    setDiagnostics(results);
    setIsRunning(false);
  };

  useEffect(() => {
    runDiagnostics();
  }, []);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado!');
  };

  const StatusIcon = ({ status }: { status: DiagnosticResult['status'] }) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-4 pt-24 space-y-6">
        {/* Status geral */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Status do Sistema</h2>
            <Button 
              onClick={runDiagnostics} 
              disabled={isRunning}
              variant="outline"
              size="sm"
            >
              {isRunning ? 'Verificando...' : 'Verificar novamente'}
            </Button>
          </div>

          <div className="space-y-3">
            {diagnostics.map((result, index) => (
              <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-muted/30">
                <StatusIcon status={result.status} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">{result.name}</span>
                    <Badge variant={
                      result.status === 'success' ? 'default' : 
                      result.status === 'error' ? 'destructive' : 
                      'secondary'
                    }>
                      {result.message}
                    </Badge>
                  </div>
                  {result.details && (
                    <p className="text-sm text-muted-foreground">{result.details}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Instruções de configuração */}
        <Card className="p-6 space-y-4">
          <h2 className="text-xl font-semibold">📋 Configuração Necessária no Spotify</h2>
          
          <Alert>
            <AlertDescription>
              Para que o OAuth funcione, você precisa adicionar os Redirect URIs corretos no Spotify Developer Dashboard.
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            <div>
              <h3 className="font-medium mb-2">1. Acesse o Spotify Dashboard</h3>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => window.open('https://developer.spotify.com/dashboard', '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Abrir Spotify Developer Dashboard
              </Button>
            </div>

            <div>
              <h3 className="font-medium mb-2">2. Adicione os Redirect URIs</h3>
              <p className="text-sm text-muted-foreground mb-3">
                No seu app, vá em <strong>Settings → Edit Settings → Redirect URIs</strong> e adicione:
              </p>
              
              {spotifyConfig?.redirectUri && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                    <code className="flex-1 text-sm font-mono break-all">
                      {spotifyConfig.redirectUri}
                    </code>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(spotifyConfig.redirectUri)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {isNative && (
                    <p className="text-xs text-muted-foreground">
                      ⚠️ Este é um deep link nativo. Certifique-se de adicioná-lo exatamente como mostrado.
                    </p>
                  )}
                </div>
              )}
            </div>

            <div>
              <h3 className="font-medium mb-2">3. Verifique o Client ID</h3>
              {spotifyConfig?.clientId && (
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <code className="flex-1 text-sm font-mono">
                    {spotifyConfig.clientId}
                  </code>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copyToClipboard(spotifyConfig.clientId)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            <div>
              <h3 className="font-medium mb-2">4. Salve e teste</h3>
              <p className="text-sm text-muted-foreground">
                Após salvar as configurações, aguarde alguns minutos e tente conectar novamente.
              </p>
            </div>
          </div>
        </Card>

        {/* Troubleshooting */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">🔧 Solução de Problemas</h2>
          
          <div className="space-y-3 text-sm">
            <div>
              <strong className="text-foreground">Botão "Aceito" não funciona?</strong>
              <p className="text-muted-foreground mt-1">
                Verifique se o Redirect URI está configurado corretamente no Spotify Dashboard. 
                Ele deve ser EXATAMENTE igual ao mostrado acima.
              </p>
            </div>

            <div>
              <strong className="text-foreground">Erro "redirect_uri_mismatch"?</strong>
              <p className="text-muted-foreground mt-1">
                O Redirect URI no código não corresponde ao configurado no Spotify. 
                Copie o URI mostrado acima e adicione no dashboard.
              </p>
            </div>

            <div>
              <strong className="text-foreground">App não abre após autorizar?</strong>
              <p className="text-muted-foreground mt-1">
                {isNative 
                  ? 'Verifique se o deep link youbetterrun:// está registrado corretamente no Info.plist (iOS) ou AndroidManifest.xml (Android).'
                  : 'Certifique-se de que o redirect URI aponta para o domínio correto do app.'}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
